import React, { useState, useContext, useRef, useEffect } from 'react';
import Head from "../components/Head";
import { Link } from "react-router-dom";
import '../styles/HomePage.css';
import productImage from '../assets/EssayWriter.png';
import auth from '../utils/auth';
import localCart from '../utils/localCartStorage';
import { CartContext } from '../contexts/CartContext';

const HomePage = () => {
  const [quantity, setQuantity] = useState(1);
  const { updateCartCount } = useContext(CartContext);
  const [popupMessage, setPopupMessage] = useState(null);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const mouseDownInsidePopup = useRef(false);
  const popupRef = useRef(null);

  const decreaseQuantity = () => {
    setQuantity(prevQuantity => Math.max(prevQuantity - 1, 1));
  };

  const increaseQuantity = () => {
    setQuantity(prevQuantity => Math.min(prevQuantity + 1, 10));
  };

  const handleAddToCart = async () => {
    if (quantity > 10) {
      alert('Du kan inte lägga till mer än 10 produkter i kundvagnen.');
      return;
    }

    try {
      if (auth.isAuthenticated()) {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cart/add`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${auth.getToken()}`
          },
          body: JSON.stringify({
            productId: 'EssayWriter Bluetooth',
            quantity: quantity,
            price: 499
          })
        });

        if (response.ok) {
          const cartData = await response.json();
          updateCartCount(cartData.cart.items.reduce((total, item) => total + item.quantity, 0));
        } else {
          const errorData = await response.json();
          alert(errorData.message || 'Ett fel uppstod när produkten skulle läggas till i kundvagnen.');
        }
      } else {
        const cart = localCart.addToCart({
          productId: 'EssayWriter Bluetooth',
          quantity: quantity,
          price: 499
        });
        updateCartCount(cart.items.reduce((total, item) => total + item.quantity, 0));
      }
    } catch (error) {
      console.error('Error adding product to cart:', error);
      alert(error.message || 'Ett fel uppstod när produkten skulle läggas till i kundvagnen.');
    }
  };

  const handleContainerMouseDown = (event) => {
    mouseDownInsidePopup.current = event.target !== event.currentTarget;
  };

  const handleContainerMouseUp = () => {
    if (!mouseDownInsidePopup.current) {
      closePopup();
    }
  };

  const handleContentMouseDown = (event) => {
    event.stopPropagation();
    mouseDownInsidePopup.current = true;
  };

  const handleContentMouseUp = (event) => {
    event.stopPropagation();
    mouseDownInsidePopup.current = true;
  };

  const openReadMorePopup = () => {
    const messageContent = (
      <div>
        <div className="home-product-features">
          <h3>Funktioner som ingår med EssayWriter Bluetooth:</h3>
          <ul>
            <li>Obegränsad textlängd (t.ex. uppsatser)</li>
            <li>Pausa-/återuppta-funktion via CAPS-knappen (om du behöver pausa skrivandet tillfälligt)</li>
            <li>Möjlighet att skriva om hela texten igen med CAPS-knappen</li>
            <li>Möjlighet att ändra skrivhastigheten</li>
            <li>Möjlighet att ändra bluetooth namnet</li>
            <li>Räckvidd upp till 10 m från datorn</li>
          </ul>
          <p className="home-compatibility">
            Kompatibel med de flesta textbehandlingsprogram för maximal användbarhet i olika situationer.
          </p>
        </div>
      </div>
    );

    setPopupMessage(messageContent);
    setIsPopupVisible(true);
  };

  const closePopup = () => {
    setIsPopupVisible(false);
    setTimeout(() => {
      setPopupMessage(null);
    }, 300);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isPopupVisible) {
        closePopup();
      }
    };

    const handleClickOutside = (e) => {
      if (popupRef.current && !popupRef.current.contains(e.target) && isPopupVisible) {
        closePopup();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isPopupVisible]);

  return (
    <div className="home-container">
      <Head title="EssayWriter | Hem" />
      <div className="home-product-section">
        <h1 className="home-essaywriter-title">EssayWriter</h1>
        <div className="home-product-content">
          <img src={productImage} alt="Produktbild" className="home-product-image" />
          <div className="home-product-details">
            <h2>Produktbeskrivning</h2>
            <p className="home-product-description">
              EssayWriter är ett kompakt digitalt transkriptionsverktyg som låter användare förladda anteckningar och trådlöst överföra dem till en dator via Bluetooth.
            </p>
            <button className="home-read-more-button" onClick={openReadMorePopup}>Läs mer...</button>
            <div className="home-price-display">
              <span className="home-product-price">499 kr</span>
            </div>
            <div className="home-purchase-section">
              <button className="home-quantity-button">
                <span className="home-quantity-display">Kvantitet: {quantity}</span>
                <span className="home-quantity-controls">
                  <span onClick={decreaseQuantity}>-</span>
                  <span onClick={increaseQuantity}>+</span>
                </span>
              </button>
              <button className="home-buy-button" onClick={handleAddToCart}>Lägg till i kundvagn</button>
            </div>
            <div className="home-legal-footer">
              <p>
                OBS! Användare ansvarar för att följa alla relevanta <Link to="/policy" className="home-legal-link">regler och etiska riktlinjer</Link> i sin användning
                av produkten. Vi tar inget ansvar för hur produkten används av kunden.
              </p>
            </div>
          </div>
        </div>
      </div>

      {popupMessage && (
        <div
          className={`home-popupContainer ${isPopupVisible ? 'visible' : 'hidden'}`}
          onMouseDown={handleContainerMouseDown}
          onMouseUp={handleContainerMouseUp}
          style={{ opacity: isPopupVisible ? 1 : 0 }}
        >
          <div
            ref={popupRef}
            className="home-popupContent"
            onMouseDown={handleContentMouseDown}
            onMouseUp={handleContentMouseUp}
            style={{ transform: isPopupVisible ? 'translateY(0)' : 'translateY(20px)' }}
          >
            <p className="home-popupTitle">Mer information</p>
            <hr className="home-divider" />
            <div className="home-popupMessage">
              {popupMessage}
            </div>
            <button className="home-popupContent-button" onClick={closePopup}>Stäng</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;